import { createSelector } from '@reduxjs/toolkit'
import { TRootState } from 'store/types'
import { isAudioItem } from 'utils/audio'
import { getDeviceVersion } from 'utils'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { overlayTypes } from 'store/features/editor/consts'

const selector = (state: TRootState) => state.editor

const activeSidebarTab = createSelector(
  selector,
  state => state.activeSidebarTab
)

const activeAsideOverlayKey = createSelector(
  selector,
  state => state.activeAsideOverlayKey
)

const activeTimelineItems = createSelector(
  selector,
  state => state.activeTimelineItems
)

const activeTimelineItemsIds = createSelector(
  activeTimelineItems,
  activeTimelineItems =>
    activeTimelineItems.flatMap(item => (item.id !== null ? [item.id] : []))
)
const hasActiveTimelineItems = createSelector(
  activeTimelineItems,
  activeTimelineItems => !!activeTimelineItems.length
)

const activeTimelineItem = createSelector(
  activeTimelineItems,
  activeTimelineItems => {
    const isOnlyOneItem = activeTimelineItems.length === 1
    if (isOnlyOneItem) {
      return activeTimelineItems[0]
    }

    return {
      id: null,
      type: null
    }
  }
)

const activeTimelineItemId = createSelector(
  activeTimelineItem,
  activeTimelineItem => activeTimelineItem.id
)

const areAllActiveTimelineItemsAudio = createSelector(
  activeTimelineItems,
  activeTimelineItems =>
    activeTimelineItems.every(item => isAudioItem(item.type))
)

const activeAudioItems = createSelector(
  activeTimelineItems,
  activeTimelineItems =>
    activeTimelineItems.filter(item => isAudioItem(item.type))
)

const activeMediaItems = createSelector(
  activeTimelineItems,
  activeTimelineItems =>
    activeTimelineItems.filter(item => !isAudioItem(item.type))
)

const timelineRulerSliceDuration = createSelector(
  selector,
  state => state.timelineRulerSliceDuration
)

const isMobileSubSidebarOpen = createSelector(
  selector,
  state => state.isMobileSubSidebarOpen
)

const isMobileInnerSliderOpen = createSelector(
  selector,
  state => state.isMobileInnerSliderOpen
)

const isTimelineItemDragging = createSelector(
  selector,
  state => state.isTimelineItemDragging
)

const isTimelineItemResizing = createSelector(
  selector,
  state => state.isTimelineItemResizing
)

const apiErrorPopupData = createSelector(
  selector,
  editorState => editorState.apiErrorPopupData
)

const isTimelineHeaderEditButtonVisible = createSelector(
  activeTimelineItem,
  activeTimelineItem => {
    const isMobile = getDeviceVersion(SMALL_DESKTOP_SIZE_BREAKPOINT)
    if (!activeTimelineItem.type) return false

    const isSelectedItemOverlayType = overlayTypes.includes(
      activeTimelineItem.type
    )

    return isMobile && isSelectedItemOverlayType
  }
)

export const isAnyBuffering = createSelector(
  selector,
  state => state.isBuffering
)

const isWelcomePopupOpen = createSelector(
  selector,
  state => state.isWelcomePopupOpen
)

const clipboard = createSelector(selector, state => state.clipboard)

export const editorSelectors = {
  activeSidebarTab,
  activeAsideOverlayKey,
  activeTimelineItem,
  activeTimelineItems,
  timelineRulerSliceDuration,
  isMobileSubSidebarOpen,
  isMobileInnerSliderOpen,
  isTimelineItemDragging,
  isTimelineItemResizing,
  isAnyBuffering,
  apiErrorPopupData,
  activeTimelineItemId,
  areAllActiveTimelineItemsAudio,
  activeAudioItems,
  activeMediaItems,
  hasActiveTimelineItems,
  activeTimelineItemsIds,
  isTimelineHeaderEditButtonVisible,
  isWelcomePopupOpen,
  clipboard
}
